import React from 'react';
import { motion } from 'framer-motion';
import bubbleVariants from './TransientThoughtBubbleVariants';

const parseGradient = (cssGradient) => {
  if (!cssGradient || typeof cssGradient !== 'string' || !cssGradient.startsWith('linear-gradient')) {
    return null;
  }
  const angleMatch = cssGradient.match(/linear-gradient\((\d+)deg/);
  const angle = angleMatch ? parseInt(angleMatch[1], 10) : 90;
  const angleRad = (angle - 90) * (Math.PI / 180);
  const x1 = Math.round(50 - Math.cos(angleRad) * 50);
  const y1 = Math.round(50 - Math.sin(angleRad) * 50);
  const x2 = Math.round(50 + Math.cos(angleRad) * 50);
  const y2 = Math.round(50 + Math.sin(angleRad) * 50);
  const colorStopRegex = /((?:rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}))\s*(\d+)?%?/gi;
  const stops = [];
  let match;
  while ((match = colorStopRegex.exec(cssGradient)) !== null) {
    stops.push({ color: match[1], offset: match[2] ? `${match[2]}%` : null });
  }
  if (stops.length > 0) {
    stops.forEach((stop, index) => {
      if (!stop.offset) stop.offset = `${(index / (stops.length - 1)) * 100}%`;
    });
  }
  return { x1, y1, x2, y2, stops };
};

const getFirstColor = (color) => {
  if (!color || typeof color !== 'string') return color || '#FABBFF';
  if (!color.startsWith('linear-gradient')) return color;
  const parsed = parseGradient(color);
  if (parsed && parsed.stops.length > 0) return parsed.stops[0].color;
  return '#FABBFF';
};

// Both bubble SVG families share the same ~2.48:1 aspect ratio.
const ASPECT = 228 / 92;

// Display width by [containerSize][text tier] so longer text gets a bigger bubble
// and the bubble always fits within its half of the screen.
const DISPLAY_WIDTH = {
  large: { short: 460, medium: 760, long: 920, xl: 1080, xxl: 1260 },
  medium: { short: 360, medium: 620, long: 780, xl: 920, xxl: 1060 },
  small: { short: 280, medium: 520, long: 660, xl: 800, xxl: 940 },
};

const getTextTier = (len) => {
  if (len < 25) return 'short';
  if (len < 60) return 'medium';
  if (len < 120) return 'long';
  if (len < 200) return 'xl';
  return 'xxl';
};

// Maps text length to the SVG variant that best fits the content.
const getSizeKey = (len) => {
  if (len < 25) return 'small';
  if (len < 30) return 'medium';
  if (len < 60) return 'large';
  if (len < 200) return 'xl';
  return 'xxl';
};

// Render the bubble SVG at the given display dimensions.
const getBubbleSvg = (config, fillColor, displayWidth, displayHeight, sizeKey) => {
  if (!config.id) return null;

  const gradientData = parseGradient(fillColor);
  const gradientId = `sbGradient-${config.id}-${Math.random().toString(36).slice(2, 7)}`;
  const actualFill = gradientData ? `url(#${gradientId})` : fillColor;

  // Speech bubbles only have top-left (tl) and bottom-right (br) SVG variants.
  // Map any other id (e.g. a cloud's tr/bl) onto the nearest valid one so a
  // mismatched config can never throw "is not a function".
  const bubbleId = config.id === 'tl' || config.id === 'tr' ? 'tl' : 'br';
  const variantFn = bubbleVariants[`${sizeKey}-${bubbleId}`] || bubbleVariants[`small-${bubbleId}`];
  if (typeof variantFn !== 'function') return null;
  const baseSvg = variantFn(actualFill);

  if (!gradientData) {
    return React.cloneElement(baseSvg, { width: displayWidth, height: displayHeight });
  }

  const { x1, y1, x2, y2, stops } = gradientData;
  const viewBox = baseSvg.props.viewBox?.split(' ').map(Number) || [0, 0, 228, 92];
  const [, , vbWidth, vbHeight] = viewBox;

  return (
    <svg
      width={displayWidth}
      height={displayHeight}
      viewBox={baseSvg.props.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={Math.round((x1 / 100) * vbWidth)}
          y1={Math.round((y1 / 100) * vbHeight)}
          x2={Math.round((x2 / 100) * vbWidth)}
          y2={Math.round((y2 / 100) * vbHeight)}
        >
          {stops.map((stop, index) => (
            <stop key={index} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
      {baseSvg.props.children}
    </svg>
  );
};

const UserName = ({ name = '', fillColor, position, windowSize }) => {
  if (!name) return null;

  const fontSizeMap = { large: 'text-2xl', medium: 'text-xl', small: 'text-lg' };
  const positionMap = {
    tl: 'absolute -bottom-8 right-2',
    br: 'absolute -top-10 left-2',
    tr: 'absolute -bottom-8 left-2',
    bl: 'absolute -top-2 right-2',
  };

  const isGradient = fillColor && typeof fillColor === 'string' && fillColor.startsWith('linear-gradient');
  const avatarStyle = isGradient ? { background: fillColor } : { backgroundColor: fillColor };

  return (
    <div
      style={{
        padding: '4px 12px 4px 4px',
        width: 'fit-content',
        borderRadius: '4px',
        border: '1px solid #333333',
        background: 'rgba(34, 34, 34, 0.8)',
        height: 'fit-content',
      }}
      className={`flex items-center ${positionMap[position]} mx-2`}
    >
      <div style={avatarStyle} className={`text-white px-2 rounded-sm mr-2 ${fontSizeMap[windowSize]}`}>
        {name[0].toUpperCase()}
      </div>
      <p className={`text-white ${fontSizeMap[windowSize]}`}>{name}</p>
    </div>
  );
};

// Lines visible per tier — grows with bubble height.
const LINE_CLAMP = { short: 2, medium: 3, long: 4, xl: 5, xxl: 6 };

const TransientThoughtSpeechBubble = ({
  config,
  fillColor,
  textColor,
  fontFamily,
  disableBubble,
  fontSize = '2.5rem',
  initiateAnimation,
  containerSize = 'large',
  transientThoughtShowUsername,
}) => {
  const handleAnimationComplete = (definition) => {
    initiateAnimation();
    disableBubble(definition);
  };

  const shadowColor = getFirstColor(fillColor);

  // Compute dimensions once so both the SVG and text container share the same values.
  const textLen = config?.words?.length || 0;
  const tier = getTextTier(textLen);
  const sizeKey = getSizeKey(textLen);
  const displayWidth = (DISPLAY_WIDTH[containerSize] ?? DISPLAY_WIDTH.large)[tier];
  const displayHeight = Math.round(displayWidth / ASPECT);

  // Cap font size so text never overflows the bubble width.
  // Text area is ~78% of displayWidth; allow ~10 chars across → max ≈ displayWidth * 0.078 px.
  const maxFontPx = Math.round(displayWidth * 0.078);
  const effectiveFontSize = `min(${fontSize}, ${maxFontPx}px)`;

  const lineClamp = LINE_CLAMP[tier] ?? 4;

  return (
    <motion.div
      animate={config.animate}
      initial={{ opacity: 0, x: 0, y: 0 }}
      style={{ filter: `drop-shadow(4px 6px 1px ${shadowColor})`, margin: '20px 0' }}
      transition={config.transition}
      onAnimationComplete={handleAnimationComplete}
      className={config.className}
    >
      {getBubbleSvg(config, fillColor, displayWidth, displayHeight, sizeKey)}

      {transientThoughtShowUsername && (
        <UserName
          position={config.id}
          fillColor={fillColor}
          name={config?.username}
          windowSize={containerSize}
        />
      )}

      <div
        lang="en"
        style={{
          fontSize: effectiveFontSize,
          fontFamily,
          hyphens: 'auto',
          lineHeight: 1.2,
          color: textColor,
          overflow: 'hidden',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
        className="absolute w-[78%] left-[11%] top-[8%] bottom-[10%] flex justify-center items-center text-center"
      >
        <div style={{
          width: '100%',
          overflow: 'hidden',
          display: '-webkit-box',
          textOverflow: 'ellipsis',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: lineClamp,
        }}>
          {config.words}
        </div>
      </div>
    </motion.div>
  );
};

export default TransientThoughtSpeechBubble;
